/**
 * ForgotPasswordPage - Password reset request and reset for admins and delegates
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { eventAPI } from '@/lib/api';
import { PublicLayout } from '@/components/layouts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Mail, KeyRound, ArrowLeft, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get params from URL. Support both new (`role`/`event_id`/`next`) and legacy (`type`/`event`) names
  // so old outstanding reset emails still work after the redirect fix.
  const roleParam = searchParams.get('role') || searchParams.get('type') || '';
  const eventIdParam = searchParams.get('event_id') || searchParams.get('event') || '';
  const resetToken = searchParams.get('token');
  const emailFromUrl = searchParams.get('email') || '';
  const nextFromUrl = searchParams.get('next') || '';   // role-aware: where to send the user after reset

  // If the reset link has an event_id or role=delegate we're in the delegate flow.
  // Falls back to admin only when neither hint is present.
  const inferredType = (eventIdParam || roleParam === 'delegate') ? 'delegate' : (roleParam || 'admin');
  const userType = inferredType;

  const [step, setStep] = useState(resetToken ? 'reset' : 'request');
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState(emailFromUrl);
  const [eventId, setEventId] = useState(eventIdParam);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState(false);

  // Compute the correct return URL for the "Continue to Login" CTA after reset.
  // Priority: explicit `next` from the link > module-specific default > generic /login.
  const returnUrl = React.useMemo(() => {
    if (nextFromUrl) return nextFromUrl;
    if (userType === 'delegate' && eventId) return `/event/${eventId}`;
    if (userType === 'member') return '/login?type=member';
    if (userType === 'admin') return '/login?type=admin';
    return '/login';
  }, [nextFromUrl, userType, eventId]);

  const returnLabel = React.useMemo(() => {
    if (returnUrl.includes('/event/')) return 'Continue to Events Login';
    if (returnUrl.includes('/studio')) return 'Continue to Studio Login';
    if (returnUrl.includes('/campaign')) return 'Continue to Campaign Login';
    if (returnUrl.includes('/school/')) return 'Continue to School Login';
    if (returnUrl.includes('/pos/')) return 'Continue to POS Login';
    if (returnUrl.includes('/security/')) return 'Continue to Security Login';
    if (returnUrl.includes('/dining/')) return 'Continue to Dining Login';
    if (returnUrl.includes('/housing/')) return 'Continue to Housing Login';
    if (returnUrl.includes('/member/')) return 'Continue to Member Login';
    return 'Continue to Login';
  }, [returnUrl]);

  // Fetch events for delegate password reset
  const { data: events } = useQuery({
    queryKey: ['events-active'],
    queryFn: () => eventAPI.list(true).then(r => r.data),
    enabled: userType === 'delegate',
  });

  // Handle request password reset
  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    if (userType === 'delegate' && !eventId) {
      toast.error('Please select an event');
      return;
    }

    setIsLoading(true);
    try {
      const endpoint = userType === 'admin' 
        ? '/api/admin/request-password-reset'
        : userType === 'member'
        ? '/api/member/request-password-reset'
        : `/api/events/${eventId}/delegate/request-password-reset`;
      
      const body = { email };

      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await res.json();
      
      if (res.ok) {
        toast.success('If your email is registered, you will receive reset instructions');
        setStep('sent');
      } else {
        toast.error(data.detail || 'Failed to send reset email');
      }
    } catch (err) {
      toast.error('Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle password reset
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setIsLoading(true);
    try {
      const endpoint = userType === 'admin' 
        ? '/api/admin/reset-password'
        : userType === 'member'
        ? '/api/member/reset-password'
        : `/api/events/${eventId}/delegate/reset-password`;
      
      const body = userType === 'admin' 
        ? { email, new_password: newPassword, reset_token: resetToken }
        : { token: resetToken, new_password: newPassword };

      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await res.json();
      
      if (res.ok) {
        setSuccess(true);
        toast.success('Password reset successfully!');
      } else {
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch (err) {
      toast.error('Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  // Success state
  if (success) {
    return (
      <PublicLayout>
        <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-900 to-slate-800">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <CardTitle>Password Reset Complete</CardTitle>
              <CardDescription>
                Your password has been successfully reset
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full" onClick={() => navigate(returnUrl)} data-testid="reset-go-to-login">
                {returnLabel}
              </Button>
            </CardContent>
          </Card>
        </div>
      </PublicLayout>
    );
  }

  // Email sent confirmation
  if (step === 'sent') {
    return (
      <PublicLayout>
        <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-900 to-slate-800">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                <Mail className="h-8 w-8 text-blue-600" />
              </div>
              <CardTitle>Check Your Email</CardTitle>
              <CardDescription>
                We've sent password reset instructions to <strong>{email}</strong>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-500 text-center">
                If you don't see the email, check your spam folder. The link expires in 1 hour.
              </p>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setStep('request')}>
                  Try Again
                </Button>
                <Button className="flex-1" onClick={() => navigate(returnUrl)} data-testid="reset-sent-continue">
                  Back to Login
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-900 to-slate-800">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" data-testid="reset-page-title">
              <KeyRound className="h-5 w-5" />
              {step === 'reset' ? 'Reset Password' : 'Forgot Password'}
              {userType === 'delegate' && (
                <span className="text-xs font-normal text-slate-500">· Events Portal</span>
              )}
            </CardTitle>
            <CardDescription>
              {step === 'reset' 
                ? (userType === 'delegate'
                    ? 'Enter your new password below. After resetting, you\'ll return to your Events Portal login.'
                    : 'Enter your new password below.')
                : userType === 'admin'
                  ? 'Enter your admin email to receive reset instructions'
                  : userType === 'member'
                    ? 'Enter the email address your church has on file and we will send you a reset link'
                    : 'Enter your email and select your event to receive reset instructions'
              }
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {step === 'request' ? (
              <form onSubmit={handleRequestReset} className="space-y-4">
                {userType === 'delegate' && (
                  <div>
                    <Label>Event</Label>
                    <Select value={eventId} onValueChange={setEventId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select your event" />
                      </SelectTrigger>
                      <SelectContent>
                        {events?.map((event) => (
                          <SelectItem key={event.id} value={event.id}>
                            {event.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                  />
                </div>
                
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Mail className="h-4 w-4 mr-2" />
                  )}
                  Send Reset Instructions
                </Button>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                </div>
                
                <div>
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                </div>
                
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <KeyRound className="h-4 w-4 mr-2" />
                  )}
                  Reset Password
                </Button>
              </form>
            )}
          </CardContent>
          
          <CardFooter className="justify-center">
            <Link to={returnUrl} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1" data-testid="reset-back-link">
              <ArrowLeft className="h-4 w-4" />
              Back to Login
            </Link>
          </CardFooter>
        </Card>
      </div>
    </PublicLayout>
  );
};

export default ForgotPasswordPage;
